import type { CustomerInfo, RsuiteSortType } from '@@partner:types:app'

import React from 'react'
import {
  normalizePhoneNumber,
  formatDate,
  formatPhone
} from '@@partner:helpers/util'

import { Link, generatePath } from 'react-router-dom'
import { Button, ButtonGroup, Table, Tag, Panel, Avatar, Stack } from 'rsuite'
import LegacyPhoneIcon from '@rsuite/icons/legacy/Phone'
import CustomerStatusLabel from '../common/CustomerStatusLabel'
import AppLoader from '@components/common/AppLoader'

import {
  ROUTE_CUSTOMERS_EDIT_CUSTOMER,
  ROUTE_CUSTOMERS_VIEW_CUSTOMER
} from '@@partner:config/routes'

import './CustomersTable.scss'

const Column = Table.Column
const HeaderCell = Table.HeaderCell
const Cell = Table.Cell

export interface CustomersTableProps {
  brandSlug: string
  customers: CustomerInfo[]
  loading?: boolean
  sortColumn: string
  sortType: RsuiteSortType | undefined
  onSortColumn: (
    sortColumn: string,
    sortType: RsuiteSortType | undefined
  ) => void
}

// Мобильная карточка клиента
function CustomerMobileCard ({ customer, brandSlug }: { customer: CustomerInfo, brandSlug: string }) {
  return (
    <Panel bordered className="mb-3 CustomerMobileCard">
      <div className="flex items-start gap-3">
        <Avatar 
          circle 
          size="md"
          className="bg-blue-500 text-white flex-shrink-0"
        >
          {customer.first_name?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-medium">
              {customer.first_name} {customer.last_name}
            </span>
            <CustomerStatusLabel status={customer.status} />
          </div>
          
          <a 
            href={`tel:${normalizePhoneNumber(customer.phone)}`}
            className="flex items-center gap-1 text-blue-600 mb-1"
          >
            <LegacyPhoneIcon /> {formatPhone(customer.phone)}
          </a>
          
          {customer.receipt_email && (
            <div className="text-sm text-gray-500 truncate">
              {customer.receipt_email}
            </div>
          )}
          
          <div className="text-xs text-gray-400 mt-2">
            ID: {customer.id} • {formatDate(new Date(customer.created_at))}
          </div>
        </div>
      </div>
      
      <div className="flex gap-2 mt-3">
        <Button
          color="green"
          appearance="primary"
          as={Link}
          to={generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, {
            brandSlug,
            customerId: customer.id
          })}
          size="sm"
          block
        >
          Просмотр
        </Button>
      </div>
    </Panel>
  )
}

export default function CustomersTable ({
  brandSlug,
  customers = [],
  loading = false,
  sortColumn,
  sortType,
  onSortColumn
}: CustomersTableProps) {
  if (loading) {
    return <AppLoader.Inner content="Загрузка таблицы" />
  }

  return (
    <>
      {/* Мобильная версия */}
      <div className="CustomersTable__mobile">
        {customers.map(customer => (
          <CustomerMobileCard 
            key={customer.id} 
            customer={customer} 
            brandSlug={brandSlug} 
          />
        ))}
        {customers.length === 0 && (
          <div className="text-gray-400 text-center py-8">Нет покупателей</div>
        )}
      </div>

      {/* Десктопная версия */}
      <div className="CustomersTable__desktop">
        <Table
          data={customers}
          onSortColumn={onSortColumn}
          sortColumn={sortColumn}
          sortType={sortType}
          autoHeight
          wordWrap
          affixHeader
          rowHeight={80}
          className="CustomersTable"
        >
          <Column sortable width={80} verticalAlign="middle">
            <HeaderCell>ИД</HeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column flexGrow={2} verticalAlign="middle">
            <HeaderCell>Клиент</HeaderCell>
            <Cell>
              {(customer: CustomerInfo) => (
                <div className="flex items-center gap-2">
                  <Avatar 
                    circle 
                    size="sm"
                    className="bg-blue-500 text-white"
                  >
                    {customer.first_name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                  <div>
                    <div className="font-medium">
                      {customer.first_name} {customer.last_name}
                    </div>
                    <a 
                      href={`tel:${normalizePhoneNumber(customer.phone)}`}
                      className="text-sm text-blue-600"
                    >
                      {formatPhone(customer.phone)}
                    </a>
                  </div>
                </div>
              )}
            </Cell>
          </Column>
          <Column flexGrow={2} verticalAlign="middle">
            <HeaderCell>Email</HeaderCell>
            <Cell dataKey="receipt_email">
              {(customer: CustomerInfo) => (
                <span className="text-gray-600">{customer.receipt_email || '—'}</span>
              )}
            </Cell>
          </Column>
          <Column sortable width={140} verticalAlign="middle">
            <HeaderCell>Статус</HeaderCell>
            <Cell dataKey="status">
              {(customer: CustomerInfo) => (
                <CustomerStatusLabel status={customer.status} />
              )}
            </Cell>
          </Column>
          <Column sortable width={120} verticalAlign="middle">
            <HeaderCell>Создан</HeaderCell>
            <Cell dataKey="created_at">
              {(customer: CustomerInfo) => (
                <span className="text-sm text-gray-500">
                  {formatDate(new Date(customer.created_at))}
                </span>
              )}
            </Cell>
          </Column>
          <Column width={100} fixed="right" verticalAlign="middle">
            <HeaderCell>Действия</HeaderCell>
            <Cell>
              {(customer: CustomerInfo) => (
                <Button
                  color="green"
                  appearance="primary"
                  as={Link}
                  to={generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, {
                    brandSlug,
                    customerId: customer.id
                  })}
                  size="xs"
                >
                  Открыть
                </Button>
              )}
            </Cell>
          </Column>
        </Table>
      </div>
    </>
  )
}
